import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import PromotionsSummaryCard from './components/PromotionsSummaryCard';
import SavedStorefrontCard from './components/SavedStorefrontCard';
import ActivePromotionCard from './components/ActivePromotionCard';
import EmptyState from './components/EmptyState';

const UserDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('storefronts');
  const [sortBy, setSortBy] = useState('recent');
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [navigate]);

  const mockPromotionsData = {
    activeCount: 5,
    expiringCount: 2
  };

  const mockSavedStorefronts = [
  {
    id: 1,
    name: "Ristorante La Dolce Vita",
    category: "Ristoranti",
    categoryIcon: "UtensilsCrossed",
    zone: "Centro Storico",
    rating: 4.8,
    distance: "0.5 km",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_192feaf14-1772570620656.png",
    imageAlt: "Elegant Italian restaurant interior with warm lighting, wooden tables set with white tablecloths, wine glasses, and rustic brick walls decorated with vintage photographs"
  },
  {
    id: 2,
    name: "Boutique Eleganza",
    category: "Moda & Abbigliamento",
    categoryIcon: "Shirt",
    zone: "Via Roma",
    rating: 4.6,
    distance: "0.8 km",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_149008e10-1772355265361.png",
    imageAlt: "Modern fashion boutique storefront with large glass windows displaying elegant clothing on mannequins, contemporary interior design with white walls and wooden fixtures"
  },
  {
    id: 3,
    name: "Caffè Centrale",
    category: "Bar & Caffè",
    categoryIcon: "Coffee",
    zone: "Piazza della Libertà",
    rating: 4.9,
    distance: "0.3 km",
    image: "https://images.unsplash.com/photo-1563808642296-39d7022e2de1",
    imageAlt: "Cozy Italian café interior with espresso machine on marble counter, wooden bar stools, shelves lined with coffee cups, and warm ambient lighting creating inviting atmosphere"
  }];


  const mockActivePromotions = [
  {
    id: 1,
    storeId: 1,
    storeName: "Ristorante La Dolce Vita",
    title: "Sconto 20% sul Menu Completo",
    discount: "-20%",
    validUntil: "2026-01-17",
    image: "https://images.unsplash.com/photo-1728078467324-f698840d7a49",
    imageAlt: "Delicious Italian pasta dish with fresh tomato sauce, basil leaves, and parmesan cheese served on white ceramic plate with fork on rustic wooden table",
    instructions: "Mostra questa promozione al momento dell'ordine.\nValida per pranzo e cena.\nNon cumulabile con altre offerte."
  },
  {
    id: 2,
    storeId: 2,
    storeName: "Boutique Eleganza",
    title: "Saldi Invernali - Fino al 50%",
    discount: "-50%",
    validUntil: "2026-01-25",
    image: "https://images.unsplash.com/photo-1650741563846-2260e0b3907e",
    imageAlt: "Stylish winter fashion collection display with elegant coats, scarves, and accessories arranged on modern clothing racks in bright boutique with natural lighting",
    instructions: "Sconto applicato automaticamente alla cassa.\nValido su articoli selezionati della collezione invernale.\nDisponibile fino ad esaurimento scorte."
  },
  {
    id: 3,
    storeId: 3,
    storeName: "Caffè Centrale",
    title: "Colazione Completa a €5",
    discount: "€5",
    validUntil: "2026-01-12",
    image: "https://images.unsplash.com/photo-1694087450396-f6b1031b37d7",
    imageAlt: "Traditional Italian breakfast spread with cappuccino in white cup, fresh croissants, orange juice, and jam on wooden tray with morning newspaper",
    instructions: "Valida dalle 7:00 alle 11:00.\nInclude: cappuccino, cornetto e spremuta.\nMostra la promozione prima di ordinare."
  }];

  const [savedStorefronts, setSavedStorefronts] = useState(mockSavedStorefronts);
  const [activePromotions, setActivePromotions] = useState(mockActivePromotions);

  const handleRemoveStorefront = (id) => {
    setSavedStorefronts((prev) => prev?.filter((store) => store?.id !== id));
  };

  const handleRemovePromotion = (id) => {
    setActivePromotions((prev) => prev?.filter((promo) => promo?.id !== id));
  };

  const sortOptions = [
  { value: 'recent', label: 'Più Recenti' },
  { value: 'name', label: 'Nome A-Z' },
  { value: 'rating', label: 'Valutazione' },
  { value: 'distance', label: 'Distanza' }];


  const categoryOptions = [
  { value: 'all', label: 'Tutte le Categorie' },
  { value: 'restaurants', label: 'Ristoranti' },
  { value: 'fashion', label: 'Moda' },
  { value: 'cafes', label: 'Bar & Caffè' },
  { value: 'culture', label: 'Cultura' },
  { value: 'health', label: 'Salute' }];


  const tabs = [
  { id: 'storefronts', label: 'Vetrine Salvate', icon: 'Store', count: savedStorefronts?.length },
  { id: 'promotions', label: 'Promozioni Attive', icon: 'Tag', count: activePromotions?.length }];


  return (
    <>
      <Helmet>
        <title>Dashboard Utente - Vetrine di Macerata</title>
        <meta name="description" content="Gestisci le tue vetrine salvate e promozioni attive su Vetrine di Macerata" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />

        <main className="pt-20 pb-24 lg:pb-8">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="mb-6 md:mb-8">
              <div className="flex items-center justify-between mb-4">
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
                  La Mia Dashboard
                </h1>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Settings"
                  iconPosition="left"
                  onClick={() => navigate('/main-storefronts')}>

                  <span className="hidden sm:inline">Impostazioni</span>
                </Button>
              </div>
              <p className="font-body text-sm md:text-base text-muted-foreground">
                Gestisci le tue vetrine preferite e promozioni
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:gap-6 mb-8">
              <PromotionsSummaryCard
                activeCount={mockPromotionsData?.activeCount}
                expiringCount={mockPromotionsData?.expiringCount} />
            </div>

            <div className="bg-card rounded-xl border border-border shadow-warm overflow-hidden">
              <div className="border-b border-border overflow-x-auto scrollbar-hide">
                <div className="flex min-w-max">
                  {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 md:px-6 py-4 font-body font-medium text-sm md:text-base transition-smooth border-b-2 flex-shrink-0 ${
                    activeTab === tab?.id ?
                    'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                    }>

                      <Icon name={tab?.icon} size={20} />
                      <span>{tab?.label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    activeTab === tab?.id ?
                    'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`
                    }>
                        {tab?.count}
                      </span>
                    </button>
                  )}
                </div>
              </div>

              <div className="p-4 md:p-6">
                {activeTab === 'storefronts' &&
                <>
                    <div className="flex flex-col sm:flex-row gap-3 mb-6">
                      <div className="flex-1">
                        <Select
                        options={sortOptions}
                        value={sortBy}
                        onChange={(e) => setSortBy(e?.target?.value)}
                        placeholder="Ordina per"
                        iconName="ArrowUpDown" />

                      </div>
                      <div className="flex-1">
                        <Select
                        options={categoryOptions}
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e?.target?.value)}
                        placeholder="Filtra per categoria"
                        iconName="Filter" />

                      </div>
                    </div>

                    {savedStorefronts?.length === 0 ?
                  <EmptyState
                    type="info"
                    icon="Store"
                    title="Nessuna vetrina salvata"
                    description="Inizia a esplorare le vetrine e salva i tuoi negozi preferiti"
                    actionLabel="Esplora Vetrine"
                    onAction={() => navigate('/main-storefronts')} /> :


                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {savedStorefronts?.map((store) =>
                    <SavedStorefrontCard
                      key={store?.id}
                      storefront={store}
                      onRemove={handleRemoveStorefront} />

                    )}
                      </div>
                  }
                  </>
                }

                {activeTab === 'promotions' &&
                <>
                    {activePromotions?.length === 0 ?
                  <EmptyState
                    type="info"
                    icon="Tag"
                    title="Nessuna promozione attiva"
                    description="Scopri le promozioni disponibili nelle vetrine"
                    actionLabel="Scopri Promozioni"
                    onAction={() => navigate('/main-storefronts')} /> :


                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {activePromotions?.map((promo) =>
                    <ActivePromotionCard
                      key={promo?.id}
                      promotion={promo}
                      onRemove={handleRemovePromotion} />

                    )}
                      </div>
                  }
                  </>
                }
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default UserDashboard;